import loadable from '@loadable/component';
import { lightThemeIconMap } from '../light';
import { IconMap, IconName } from '../types';
/**
 * Dark store icon map
 *
 * **Quick Start Approach: Reusing light theme icons with dark theme colors**
 *
 * The dark icon map inherits from light theme icon map by default.
 * Since dark theme uses the same color token names (textPrimary, secondary, primary40, etc.)
 * but with different values, icons automatically adapt to dark colors:
 *
 * Light theme:
 * - textPrimary: Dark color (#142536)
 * - secondary: Pink (#A61C54)
 * - primary40: Medium gray (#596875)
 *
 * Dark theme:
 * - textPrimary: White (#FFFFFF)
 * - secondary: Light pink (#F06292)
 * - primary40: Medium gray
 *
 * **To add dark-specific icons:**
 * 1. Create dark SVG icons in src/shared/svg/dark/
 * 2. Import them here
 * 3. Override specific icons using spread syntax:
 *
 * @example
 * import DarkLogo from 'shared/svg/dark/DarkLogo';
 * import DarkProfile from 'shared/svg/dark/DarkProfile';
 *
 * export const darkThemeIconMap: IconMap = {
 *   ...lightThemeIconMap,
 *   [IconName.HOME]: {
 *     default: DarkLogo,
 *     defaultColor: 'textPrimary',  // Use dark white
 *   },
 *   [IconName.ACCOUNT]: {
 *     default: DarkProfile,
 *     defaultColor: 'textPrimary',  // Use dark white
 *     filled: DarkProfileFilled,
 *     filledColor: 'secondary',      // Use dark pink
 *   },
 *   // Can also override just colors without changing icons
 *   [IconName.WISHLIST]: {
 *     ...lightThemeIconMap[IconName.WISHLIST],
 *     filledColor: 'secondary',      // Use dark pink for filled wishlist
 *   },
 * };
 */
export const darkThemeIconMap: IconMap = {
  ...lightThemeIconMap,
  [IconName.CIRCLE_X_DUOTONE]: {
    default: loadable(() => import('shared/svg/CircleXDuotone')),
    defaultColor: 'textPrimary',
  },
  [IconName.CROSS_ICON]: {
    default: loadable(() => import('shared/svg/CrossIcon')),
    defaultColor: 'stateDisabled',
  },
  [IconName.CHAT_BUBBLE_V2]: {
    default: loadable(() => import('shared/svg/ChatBubbleV2')),
    defaultColor: 'currentColor',
  },
  [IconName.REDIRECT_ARROW]: {
    default: loadable(() => import('shared/svg/RedirectArrow')),
    defaultColor: 'currentColor',
  },
};
